/**
 * useContentPlaybackTracker
 *
 * Single home for all content-playback side effects.
 * Reads currentTime directly from the video element via videoElementRegistry
 * (Redux playbackStates.currentTime is not updated during continuous playback).
 * Most side effects are timeupdate-driven and gated by a `useRef` one-shot
 * flag so they fire at most once per content session; the saveToWatchHistory
 * side effect is the exception — it fires on every pause/dispose/tab-close
 * because every save legitimately wants the latest position.
 *
 * Side effects (each extracted into its own helper below):
 *   - typeLimit gate: pauses video when currentTimeMs >= typeLimit AND user
 *     lacks exclusive rights; auto-resumes when rights are gained.
 *   - processFeedTagsWatch on watch threshold (Task 1)
 *   - incrementContentViews on watch threshold (Task 2)
 *   - default gift suggestion during playback (Task 3)
 *   - saveToWatchHistory on every pause (native DOM pause event) and on
 *     unmount/dispose — gated by the 5s threshold; each save reflects the
 *     latest position. App-global tab-close / visibility-hidden lifecycle
 *     pauses all videos (see videoManagementSlice), which falls through the
 *     same native `pause` path. (Task 5)
 *
 * Flutter sources of truth:
 *   - lib/presentation/view_models/content_view_model.dart (watch-progress
 *     side effects: feed tags, views, default gift, NSFW gate)
 *   - lib/domain/notifiers/video_player_notifier.dart (typeLimit /
 *     `_checkVideoPlaybackExclusiveRights`, `_saveToWatchHistory`)
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import { videoElementRegistry } from '@/presentation/registries/videoElementRegistry';
import { useVideoManagement } from '@/presentation/feature/content/hooks/useVideoManagement';
import { processFeedTagsWatchUseCase } from '@/domain/usecases/feedTags/processFeedTagsWatchUseCase';
import { incrementContentViewsUseCase } from '@/domain/usecases/content/incrementContentViewsUseCase';
import { saveToWatchHistoryUseCase } from '@/domain/usecases/content/saveToWatchHistoryUseCase';
import { FetchGiftsUseCase } from '@/domain/usecases/gift/fetchGiftsUseCase';
import { giftService } from '@/domain/services/gift/giftService';
import type { GiftEntity } from '@/domain/entities/gift/giftEntity';
import { remoteConfigService } from '@/domain/services/remoteConfig/remoteConfigService';
import { ANIMATION_CONSTANTS } from '@/presentation/constants/animationConstants';
import { eventBus, APP_EVENT_KEYS } from '@/infrastructure/eventBus';
import { appLogger } from '@/domain/services/analytics/appLogger';

const fetchGiftsUseCase = new FetchGiftsUseCase(giftService);

interface UseContentPlaybackTrackerParams {
  videoKey: string;
  /** typeLimit in milliseconds (null/undefined = no limit) */
  typeLimitMs: number | null | undefined;
  hasExclusiveRights: boolean;
  /** True once the video element is registered and playback has started */
  isVideoReady: boolean;
  /** Content/media id (`media.id`) */
  contentId: string;
  /** Whether the media is content (`media.isContent`) */
  isContent: boolean;
  /** Whether the current user owns this content (caller computes this) */
  isOwner: boolean;
  /** Pre-computed: media has at least one contentTag or hashTag */
  hasContentOrHashTags: boolean;
  /** Total media duration in milliseconds (`media.duration ?? 0`) */
  durationMs: number;
  /** Content owner's firebase user id (`media.firebaseId`); needed by `incrementContentViews` */
  ownerUserId: string | undefined;
  /** Current views count (`media.views`); used to compute the optimistic local bump */
  currentViews: number | undefined;
}

// --- Side-effect helpers ---------------------------------------------------
// Each helper encapsulates one timeupdate-driven side effect. They take the
// per-instance refs/setters they mutate so they remain pure, testable, and
// independent of React state (no closures over component-scope variables).
// Failures are logged and swallowed: the one-shot ref is intentionally not
// reset, since a retry would almost certainly hit the same error.

/**
 * typeLimit gate. Mirrors Flutter's
 * `VideoPlayerNotifier._checkVideoPlaybackExclusiveRights`
 * (lib/domain/notifiers/video_player_notifier.dart).
 */
function checkTypeLimit(args: {
  videoKey: string;
  typeLimitMs: number | null | undefined;
  hasExclusiveRights: boolean;
  currentTimeMs: number;
  reachedRef: MutableRefObject<boolean>;
  setReached: Dispatch<SetStateAction<boolean>>;
  pauseVideo: (key: string, opts: { forced?: boolean }) => void;
}): void {
  const { typeLimitMs, hasExclusiveRights, currentTimeMs, reachedRef, setReached, pauseVideo, videoKey } = args;
  if (typeLimitMs == null || typeLimitMs <= 0 || hasExclusiveRights) return;
  if (currentTimeMs < typeLimitMs) return;

  if (!reachedRef.current) {
    reachedRef.current = true;
    setReached(true);
  }
  pauseVideo(videoKey, { forced: true });
}

/**
 * typeLimit gate for seek attempts. Same predicate as `checkTypeLimit`, but
 * fires on the native `seeking` event so we can intercept a scrub past the
 * limit before the browser plays anything at the seeked position. Required
 * for native fullscreen, where the browser/OS scrubber bypasses our in-app
 * `handleSeek` guard. Pauses synchronously and snaps `currentTime` back to
 * the limit boundary.
 */
function checkTypeLimitOnSeek(args: {
  videoKey: string;
  videoEl: HTMLVideoElement;
  typeLimitMs: number | null | undefined;
  hasExclusiveRights: boolean;
  reachedRef: MutableRefObject<boolean>;
  setReached: Dispatch<SetStateAction<boolean>>;
  pauseVideo: (key: string, opts: { forced?: boolean }) => void;
}): void {
  const { videoEl, typeLimitMs, hasExclusiveRights, reachedRef, setReached, pauseVideo, videoKey } = args;
  if (typeLimitMs == null || typeLimitMs <= 0 || hasExclusiveRights) return;
  const currentTimeMs = videoEl.currentTime * 1000;
  if (currentTimeMs < typeLimitMs) return;

  // Pause directly on the element — synchronous, beats the browser's
  // post-seek auto-resume that timeupdate-based pauses can't.
  videoEl.pause();
  // Snap back to the limit so the user can't ride past it via a native
  // scrubber. Setting currentTime triggers another `seeking` event, but the
  // recursion is bounded: once at the limit, the no-op assignment below
  // doesn't re-fire, and the predicate above blocks any further work.
  videoEl.currentTime = typeLimitMs / 1000;

  if (!reachedRef.current) {
    reachedRef.current = true;
    setReached(true);
  }
  pauseVideo(videoKey, { forced: true });
}

/**
 * processFeedTagsWatch on watch threshold (Task 1).
 * Mirrors lib/presentation/view_models/content_view_model.dart:325-344.
 * Errors are logged and swallowed so the other side effects keep running.
 */
async function checkProcessFeedTagsWatch(args: {
  watchSetRef: MutableRefObject<boolean>;
  currentTimeSeconds: number;
  durationSeconds: number;
  contentId: string;
  isContent: boolean;
  isOwner: boolean;
  hasContentOrHashTags: boolean;
}): Promise<void> {
  const { watchSetRef, currentTimeSeconds, durationSeconds, contentId, isContent, isOwner, hasContentOrHashTags } = args;
  if (watchSetRef.current) return;
  if (!contentId || !isContent || isOwner || !hasContentOrHashTags) return;
  if (durationSeconds <= 0) return;
  if (currentTimeSeconds > durationSeconds - 1) return;

  const meetsTimeThreshold =
    currentTimeSeconds >= remoteConfigService.preferredContentTagsWatchMinSeconds ||
    currentTimeSeconds >= (durationSeconds * remoteConfigService.preferredContentTagsWatchMinPercentage) / 100;
  if (!meetsTimeThreshold) return;

  watchSetRef.current = true;
  try {
    await processFeedTagsWatchUseCase.execute(contentId);
  } catch (error) {
    appLogger.error('Failed to process feed tags watch', 'useContentPlaybackTracker.checkProcessFeedTagsWatch', { error: error as Error });
  }
}

/**
 * incrementContentViews on watch threshold (Task 2).
 * Mirrors lib/presentation/view_models/content_view_model.dart:347-361.
 * Errors are logged and swallowed so the other side effects keep running.
 */
async function checkIncrementContentViews(args: {
  setRef: MutableRefObject<boolean>;
  currentTimeSeconds: number;
  durationSeconds: number;
  contentId: string;
  isContent: boolean;
  isOwner: boolean;
  ownerUserId: string | undefined;
  currentViews: number | undefined;
}): Promise<void> {
  const { setRef, currentTimeSeconds, durationSeconds, contentId, isContent, isOwner, ownerUserId, currentViews } = args;
  if (setRef.current) return;
  if (!contentId || !isContent || isOwner || !ownerUserId) return;
  if (durationSeconds <= 0) return;
  if (currentTimeSeconds > durationSeconds - 1) return;
  if (currentTimeSeconds < remoteConfigService.updateViewsWatchMinSeconds) return;

  setRef.current = true;
  const bumpedViews = (currentViews ?? 0) + 1;
  try {
    const success = await incrementContentViewsUseCase.execute(ownerUserId, contentId);
    if (success) {
      eventBus.emit(APP_EVENT_KEYS.CONTENT_ITEM_UPDATED, {
        mediaId: contentId,
        patch: { views: bumpedViews },
      });
    }
  } catch (error) {
    appLogger.error('Failed to increment content views', 'useContentPlaybackTracker.checkIncrementContentViews', { error: error as Error });
  }
}

/**
 * Default-gift suggestion during playback (Task 3).
 * Mirrors lib/presentation/view_models/content_view_model.dart:363-379.
 * Flutter only flips `_defaultGiftShown` when the cache returned at least one
 * gift, so we mirror that semantic — `loadingRef` covers the in-flight gap so
 * timeupdate doesn't re-enter the fetch.
 */
async function checkDefaultGift(args: {
  shownRef: MutableRefObject<boolean>;
  loadingRef: MutableRefObject<boolean>;
  currentTimeSeconds: number;
  durationSeconds: number;
  isOwner: boolean;
  setDefaultGift: Dispatch<SetStateAction<GiftEntity | undefined>>;
}): Promise<void> {
  const { shownRef, loadingRef, currentTimeSeconds, durationSeconds, isOwner, setDefaultGift } = args;
  if (shownRef.current || loadingRef.current || isOwner) return;

  const meetsTimeThreshold =
    currentTimeSeconds >= ANIMATION_CONSTANTS.duration.SHOW_DEFAULT_GIFT_SECONDS ||
    currentTimeSeconds > durationSeconds / 2;
  if (!meetsTimeThreshold) return;

  loadingRef.current = true;
  try {
    const gifts = await fetchGiftsUseCase.execute();
    if (gifts.length > 0) {
      const giftIndex = Math.floor(Math.random() * gifts.length);
      setDefaultGift(gifts[giftIndex]);
      shownRef.current = true;
    }
  } catch (error) {
    appLogger.error('Failed to load default gift', 'useContentPlaybackTracker.checkDefaultGift', { error: error as Error });
  } finally {
    loadingRef.current = false;
  }
}

// --- Hook ------------------------------------------------------------------

export function useContentPlaybackTracker({
  videoKey,
  typeLimitMs,
  hasExclusiveRights,
  isVideoReady,
  contentId,
  isContent,
  isOwner,
  hasContentOrHashTags,
  durationMs,
  ownerUserId,
  currentViews,
}: UseContentPlaybackTrackerParams): { typeLimitReached: boolean; defaultGift: GiftEntity | undefined } {
  const { pauseVideo, playVideo } = useVideoManagement();

  const [typeLimitReached, setTypeLimitReached] = useState(false);
  const typeLimitReachedRef = useRef(false);

  // One-shot flag for processFeedTagsWatch (Flutter's `_preferredContentTagsWatchSet`).
  const feedTagsWatchSetRef = useRef(false);

  // One-shot flag for incrementContentViews (Flutter's `_updateContentViewsSet`).
  const updateContentViewsSetRef = useRef(false);

  // Default-gift one-shot state (Flutter's `_defaultGiftShown` + `defaultGiftNotifier`).
  const [defaultGift, setDefaultGift] = useState<GiftEntity | undefined>(undefined);
  const defaultGiftShownRef = useRef(false);
  const defaultGiftLoadingRef = useRef(false);

  // Memoize the pause handler so the timeupdate listener doesn't go stale
  const pauseVideoRef = useRef(pauseVideo);
  pauseVideoRef.current = pauseVideo;

  // Saves the current watch position. Triggered by the native `pause` event
  // (covers manual pause, auto-pause, and lifecycle-driven pauseAll from
  // videoManagementSlice) and by the unmount-only effect below (covers
  // dispose, since browsers don't fire `pause` when a <video> is removed
  // from the DOM). Gated by the 5s threshold; each call reflects the latest
  // position.
  const saveWatchHistory = useCallback(() => {
    const video = videoElementRegistry.get(videoKey);
    if (!video) return;
    const currentTimeMs = video.currentTime * 1000;
    if (currentTimeMs < remoteConfigService.saveToWatchHistoryMinSeconds * 1000) return;
    if (!contentId) return;
    void saveToWatchHistoryUseCase
      .execute(contentId, Math.round(currentTimeMs), durationMs)
      .catch((error) => {
        // The use case already swallows errors and never throws, but be
        // defensive in case the contract changes.
        appLogger.error('Failed to save to watch history', 'useContentPlaybackTracker.saveWatchHistory', { error: error as Error });
      });
  }, [videoKey, contentId, durationMs]);

  // Holds the latest saveWatchHistory for the unmount-only effect below,
  // which has []-deps and would otherwise capture a stale closure.
  const saveWatchHistoryRef = useRef(saveWatchHistory);
  saveWatchHistoryRef.current = saveWatchHistory;

  const handleTimeUpdate = useCallback(async (e: Event) => {
    const video = e.target as HTMLVideoElement;
    const currentTimeMs = video.currentTime * 1000;
    const currentTimeSeconds = video.currentTime;
    const durationSeconds = durationMs / 1000;

    checkTypeLimit({
      videoKey,
      typeLimitMs,
      hasExclusiveRights,
      currentTimeMs,
      reachedRef: typeLimitReachedRef,
      setReached: setTypeLimitReached,
      pauseVideo: pauseVideoRef.current,
    });

    void checkProcessFeedTagsWatch({
      watchSetRef: feedTagsWatchSetRef,
      currentTimeSeconds,
      durationSeconds,
      contentId,
      isContent,
      isOwner,
      hasContentOrHashTags,
    });

    void checkIncrementContentViews({
      setRef: updateContentViewsSetRef,
      currentTimeSeconds,
      durationSeconds,
      contentId,
      isContent,
      isOwner,
      ownerUserId,
      currentViews,
    });

    void checkDefaultGift({
      shownRef: defaultGiftShownRef,
      loadingRef: defaultGiftLoadingRef,
      currentTimeSeconds,
      durationSeconds,
      isOwner,
      setDefaultGift,
    });
  }, [
    videoKey,
    typeLimitMs,
    hasExclusiveRights,
    contentId,
    isContent,
    isOwner,
    hasContentOrHashTags,
    durationMs,
    ownerUserId,
    currentViews,
  ]);

  // Attach timeupdate listener to the video element whenever the video is
  // ready. We attach unconditionally (rather than gating on typeLimit) because
  // multiple side effects share this handler — each block has its own guards.
  useEffect(() => {
    if (!isVideoReady) return;

    const videoElement = videoElementRegistry.get(videoKey);
    if (!videoElement) return;

    const handleSeeking = (e: Event) => {
      checkTypeLimitOnSeek({
        videoKey,
        videoEl: e.target as HTMLVideoElement,
        typeLimitMs,
        hasExclusiveRights,
        reachedRef: typeLimitReachedRef,
        setReached: setTypeLimitReached,
        pauseVideo: pauseVideoRef.current,
      });
    };

    videoElement.addEventListener('timeupdate', handleTimeUpdate);
    videoElement.addEventListener('pause', saveWatchHistory);
    videoElement.addEventListener('seeking', handleSeeking);

    // Also check current position immediately (video may already be past
    // typeLimit). Only the typeLimit block has an "at-attach" path because it
    // is the only side effect that needs to act on a pre-existing state.
    checkTypeLimit({
      videoKey,
      typeLimitMs,
      hasExclusiveRights,
      currentTimeMs: videoElement.currentTime * 1000,
      reachedRef: typeLimitReachedRef,
      setReached: setTypeLimitReached,
      pauseVideo: pauseVideoRef.current,
    });

    return () => {
      videoElement.removeEventListener('timeupdate', handleTimeUpdate);
      videoElement.removeEventListener('pause', saveWatchHistory);
      videoElement.removeEventListener('seeking', handleSeeking);
      // Intentionally NOT calling saveWatchHistory here: this effect re-runs
      // whenever any of its deps change mid-playback (e.g. currentViews bumps
      // after incrementContentViews succeeds), and a save in this cleanup
      // would fire a phantom cloud call. Unmount/dispose is handled by the
      // []-deps effect below.
    };
  }, [videoKey, typeLimitMs, hasExclusiveRights, isVideoReady, handleTimeUpdate, saveWatchHistory]);

  // Final save on actual unmount/dispose. The native `pause` event covers
  // every other path; this []-deps effect runs cleanup exactly once when the
  // hook unmounts, reading the latest closure via the ref.
  useEffect(() => {
    return () => saveWatchHistoryRef.current();
  }, []);

  // Auto-resume when rights are gained (unlock or subscription)
  useEffect(() => {
    if (!typeLimitReachedRef.current) return;
    if (!hasExclusiveRights) return;

    typeLimitReachedRef.current = false;
    setTypeLimitReached(false);
    void playVideo(videoKey);
  }, [hasExclusiveRights, videoKey, playVideo]);

  return { typeLimitReached, defaultGift };
}
